import { createAsyncThunk } from "@reduxjs/toolkit";
import { APICall } from "@/config/NetworkUtility";
import { logApiError } from "@/services/apiErrorLogger";

const CACHE_TTL = 5 * 60 * 1000; // 5 min — tune as needed

const DROPDOWN_CACHE_TIME = 5 * 60 * 1000;

/* ==========================================
   GET BASE NAVIGATIONS
========================================== */

export const getBaseNavigations = createAsyncThunk(
    "baseNavigation/getBaseNavigations",
    async (payload, { rejectWithValue, getState }) => {
        try {

            const {
                forceRefresh = false,
                ...queryPayload
            } = payload;


            const cacheKey =
                new URLSearchParams(
                    Object.entries(
                        queryPayload
                    )
                        .filter(
                            ([, value]) =>
                                value !==
                                undefined &&
                                value !== null
                        )
                        .sort(
                            (
                                [keyA],
                                [keyB]
                            ) =>
                                keyA.localeCompare(
                                    keyB
                                )
                        )
                ).toString();

            const cached = getState().baseNavigations?.cache?.[cacheKey];

            const isCacheValid = cached && Date.now() - cached.timestamp < CACHE_TTL

            if (isCacheValid && !forceRefresh) {
                return {
                    data: cached.data,
                    pagination: cached.pagination,
                    cacheKey,
                    fromCache: true,
                };
            }

            const queryParams = new URLSearchParams({
                ...payload,
            }).toString();

            const url = `v1/NavigationSetup/GetBaseNavigationList?${queryParams}`;

            const result = await APICall("get", payload, url);

            if (result?.statuscode === 200 || result?.statuscode === 201) {
                return {
                    data: result?.data || [],
                    pagination: result?.pagination || {},
                    cacheKey,
                    fromCache: false,
                };
            }

            return rejectWithValue(result);
        } catch (error) {
            logApiError(error, {
                source: "base navigations",
                endpoint: "getBaseNavigations",
                payload,
            });

            return rejectWithValue(error);
        }
    },
);

/* ==========================================
   CREATE BASE NAVIGATION
========================================== */

export const createBaseNavigation = createAsyncThunk(
    "baseNavigation/createBaseNavigation",
    async (payload, { rejectWithValue }) => {
        try {
            const result = await APICall(
                "post",
                payload,
                "v1/NavigationSetup/BaseNavigationInsert",
            );

            if (result?.statuscode === 200 || result?.statuscode === 201) {
                return result;
            }

            return rejectWithValue(result);
        } catch (error) {
            logApiError(error, {
                source: "base navigations",
                endpoint: "createBaseNavigation",
                payload,
            });

            return rejectWithValue(error);
        }
    },
);

/* ==========================================
   UPDATE BASE NAVIGATION
========================================== */

export const updateBaseNavigation = createAsyncThunk(
    "baseNavigation/updateBaseNavigation",
    async (payload, { rejectWithValue }) => {
        try {
            const result = await APICall(
                "post",
                payload,
                "v1/NavigationSetup/BaseNavigationUpdate",
            );

            if (result?.statuscode === 200 || result?.statuscode === 201) {
                return result;
            }

            return rejectWithValue(result);
        } catch (error) {
            logApiError(error, {
                source: "base navigations",
                endpoint: "updateBaseNavigation",
                payload,
            });

            return rejectWithValue(error);
        }
    },
);

/* ==========================================
   GET BASE NAVIGATION DROPDOWN LIST
========================================== */

export const getBaseNavigationDropdownList = createAsyncThunk(
    "baseNavigation/getBaseNavigationDropdownList",
    async (payload, { rejectWithValue, getState }) => {
        try {

            const { forceRefresh = false, ...queryPayload } = payload;

            const cacheKey = new URLSearchParams(
                Object.entries(queryPayload)
                    .filter(([, value]) => value !== undefined && value !== null)
                    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
            ).toString();

            const cached = getState()?.baseNavigations?.dropdownCache?.[cacheKey]

            const isCacheValid = cached && Date.now() - cached.timestamp < DROPDOWN_CACHE_TIME;

            if (isCacheValid && !forceRefresh) {
                return {
                    data: cached.data,
                    cacheKey,
                    fromCache: true,
                }
            }

            const queryParams =
                new URLSearchParams(
                    Object.entries(
                        queryPayload
                    ).filter(
                        ([, value]) =>
                            value !==
                            undefined &&
                            value !== null
                    )
                ).toString();

            const url = `v1/NavigationSetup/GetBaseNavigationDropdownList?${queryParams}`;

            const result = await APICall("get", payload, url);

            if (result?.statuscode === 200 || result?.statuscode === 201) {
                return {
                    data: result?.data?.baseNavigationList || [],
                    cacheKey,
                    fromCache: false
                }
            }

            return rejectWithValue(result);
        } catch (error) {
            logApiError(error, {
                source: "base navigation dropdown",
                endpoint: "getBaseNavigationDropdownList",
                payload,
            });

            return rejectWithValue(error);
        }
    },
);

/* ==========================================
   DELETE BASE NAVIGATION
========================================== */

export const deleteBaseNavigation = createAsyncThunk(
    "baseNavigation/deleteBaseNavigation",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await APICall(
                "post",
                payload,
                "v1/NavigationSetup/DeleteBaseNavigation",
            );

            if (![200, 201].includes(response?.statuscode)) {
                return rejectWithValue({
                    status: response?.statuscode,
                    message: response?.message ?? "Failed to delete base navigation.",
                    data: response?.data ?? null,
                });
            }
            return response;
        } catch (error) {
            logApiError(error, {
                source: "base navigations",
                endpoint: "deleteBaseNavigation",
                payload,
            });

            return rejectWithValue(error);
        }
    },
);